import { ApolloQueryResult } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { Option } from "@components/fields/AsyncSelect";
import { searchOptions as searchForOptions } from "@utils/searchUtils";

type Props<T extends unknown> = {
  defaultOptions: Option[];
  excludeValue?: string;
  getLabel: (t: T | null) => string;
  getValue: (t: T | null) => string;
  initialOption: { label?: string | null; value?: string | null };
  loadOptionsOnOpen?: boolean;
  sortOptions?: boolean;
  takeDefaultLabel?: boolean;
};

const useAsyncSearch = <T extends unknown>({
  defaultOptions,
  excludeValue,
  getLabel,
  getValue,
  initialOption,
  loadOptionsOnOpen = false,
  sortOptions = true,
  takeDefaultLabel = true
}: Props<T>) => {
  const intl = useIntl();

  const [currentOption, setCurrentOption] = useState<Option | null>({
    label: initialOption.label ? initialOption.label : takeDefaultLabel ? defaultOptions?.[0]?.label ?? "" : "",
    value: initialOption.value ?? ""
  });

  const searchOptions = useCallback(
    async (searchTerm: string, query: Promise<ApolloQueryResult<unknown>>) =>
      searchForOptions<T>({
        defaultOptions,
        excludeValue,
        getLabel,
        getValue,
        loadOptionsOnOpen,
        query,
        searchTerm,
        sortOptions
      }),
    [defaultOptions, excludeValue, getLabel, getValue, loadOptionsOnOpen, sortOptions]
  );

  const allDefaultOptions = useMemo(() => {
    const startTypingOption: Option = {
      disabled: true,
      label: intl.formatMessage({ id: "dictionary.startTypingForOptions" }),
      value: "..."
    };
    return [startTypingOption].concat(defaultOptions);
  }, [defaultOptions, intl]);

  return {
    currentOption,
    defaultOptions: allDefaultOptions,
    searchOptions,
    setCurrentOption
  };
};

// eslint-disable-next-line @typescript-eslint/no-use-before-define

export default useAsyncSearch;
